import { IconArrowRight, IconSpaces, IconUsers, IconUsersGroup, IconVideo } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Modal, Text } from '@fragments';
import { CommunityBanner, UserAvatar } from '@components/Community';

import './styles.scss';

const COUNTER_META = {
    members: { label: 'common:entities.members', Icon: IconUsers },
    content: { label: 'common:entities.contents', Icon: IconVideo },
    groups: { label: 'common:entities.groups', Icon: IconUsersGroup },
};

/**
 * Pure view of the invitation. Three stacked layers, like the home tiles:
 * blurred banner backdrop → mask-faded hero flush to the modal's top corners →
 * centered content (logo, name, owner line, counters). Actions live in the
 * card modal's vertical footer.
 */
export default function InviteModalView({ open, community, owner, counters, joining, join, decline, close }) {
    const { t } = useTranslation('community');

    if (!community) return null;

    const hero = community.hero_condensed || community.hero;

    return (
        <Modal
            open={open}
            onClose={close}
            variant="card"
            size="sm"
            closeLabel={t('invite.close')}
            className="community-invite-modal"
            // The banner, blurred, as ambient color under the whole surface —
            // body and footer alike.
            backdrop={community.banner && (
                <span
                    className="community-invite__backdrop"
                    style={{ backgroundImage: `url(${community.banner})` }}
                />
            )}
            footer={
                <>
                    <Button
                        color="inverse"
                        size="large"
                        fullWidth
                        loading={joining}
                        preventLoading
                        endIcon={<IconArrowRight />}
                        onClick={join}
                    >
                        {t('invite.join')}
                    </Button>
                    <Button
                        color="overlay"
                        variant="text"
                        size="medium"
                        fullWidth
                        disabled={joining}
                        onClick={decline}
                    >
                        {t('invite.decline')}
                    </Button>
                </>
            }
        >
            <div className="community-invite">
                {/* Layer 2 (layer 1 is the modal backdrop above): hero touching
                    the surface's top corners, dissolving toward the content
                    through an alpha mask. */}
                <div className="community-invite__hero" aria-hidden="true">
                    {hero ? <img src={hero} alt="" /> : <IconSpaces />}
                </div>

                {/* Layer 3: centered content rising out of the hero. */}
                <div className="community-invite__content">
                    <CommunityBanner banner={community.banner} size={80} className="community-invite__logo" />

                    <div className="community-invite__heading">
                        <Text.subheading contrast={6}>{t('invite.eyebrow')}</Text.subheading>
                        <Text.h3 weight={500} contrast={10}>{community.name}</Text.h3>
                    </div>

                    <div className="community-invite__owner">
                        {owner && <UserAvatar user={owner} size={24} />}
                        <Text.span contrast={7}>
                            {t('invite.message', { owner: owner?.username || community.name, name: community.name })}
                        </Text.span>
                    </div>

                    <dl className="community-invite__stats">
                        {counters.map(({ key, value, loading }) => {
                            const { label, Icon } = COUNTER_META[key];
                            return (
                                <div key={key} className="community-invite__stat">
                                    <dt>
                                        <Text.caption contrast={6} left={<Icon />}>{t(label)}</Text.caption>
                                    </dt>
                                    <dd>
                                        <Text.h4 weight={500} contrast={10} loading={loading}>{value}</Text.h4>
                                    </dd>
                                </div>
                            );
                        })}
                    </dl>
                </div>
            </div>
        </Modal>
    );
}
